import React, {
  useState,
  useEffect,
  useContext,
} from "react";
import { useParams, useHistory } from "react-router";
import {
  Box,
  Button,
  Stack,
  TextField,
} from "@mui/material";
import moment from "moment";

import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

import { DatePicker } from "@mui/x-date-pickers/DatePicker";

import Swal from "sweetalert2";
import Toast from "../../utils/Toast";

import { AuthContext } from "../../utils/userProvider";

import useConsent from "../../queries/consent/getConsent";
import useCreateAudit from "../../mutation/audit/createAudit";
import {
  FormPageShell,
  FormPageCard,
  FormPageHeader,
  FormActions,
} from "../../components/ListPage";

const schema = yup.object().shape({
  first_name: yup.string().required("First Name is required"),
  last_name: yup.string().required("Last Name is required"),
  email_id: yup.string().email("Invalid email format").required("Email is required"),
  mobile_number: yup.string().required("Mobile number is required"),
  consent_withdrawn_date: yup.string().nullable(),
  remarks: yup.string().nullable(),
});

const EditConsentData = () => {
  const { id } = useParams();
  const history = useHistory();
  const [response, resetConsent] = useConsent(id);
  const { authState, setAuthState } = useContext(AuthContext);

  const [auditResponse, setAuditData] = useCreateAudit();

  const [currentData, setCurrentData] = useState();
  const [snapshotCurrent, setSnapshotCurrent] = useState({});

  const { control, register, handleSubmit, setValue, errors } = useForm({
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    console.log(errors);
  }, [errors]);

  useEffect(() => {
    if (response.data) {
      setSnapshotCurrent(response.data);
      setCurrentData(response.data.id);
      setValue("first_name", response.data.first_name || "");
      setValue("last_name", response.data.last_name || "");
      setValue("email_id", response.data.email_id || "");
      setValue("mobile_number", response.data.mobile_number || "");
      setValue("consent_withdrawn_date", response.data.consent_withdrawn_date || "");
      setValue("remarks", response.data.remarks || "");
    }
  }, [response]);

  const goBack = () => {
    window.location.href = "/dashboard/consent-data";
  };

  const onSubmit = (values) => {
    console.log(values);

    Swal.fire({
      title: "Create Updates",
      html: `<h4>Please provide a description before submitting your changes</h4>
              <textarea maxlength="100" type="text" id="actionDescription" class="swal2-input" placeholder="Description" font-size=18px style="height:auto!important" rows=5>`,
      confirmButtonText: "Confirm",
      focusConfirm: false,
      preConfirm: () => {
        const actionDescription =
          Swal.getPopup().querySelector("#actionDescription").value;
        if (!actionDescription) {
          Swal.showValidationMessage(`Please enter description`);
        }
        return {
          actionDescription: actionDescription,
          newData: values,
          snapshotCurrent: snapshotCurrent,
          currentData: currentData,
          type: "consent-data",
          action: "update",
        };
      },
    }).then((result) => {
      if (result.isConfirmed) {
        setAuditData(result.value);
      }
    });
  };

  useEffect(() => {
    try {
      if (auditResponse.data) {
        Toast.fire({
          icon: "success",
          title: "Consent Data update Audit Created",
        });
        history.push("/dashboard/consent-data");
      } else if (auditResponse.error) {
        let data = auditResponse.errorMessage.response.data;
        if (data) {
          let messageData = data.message;
          let message = messageData.message;
          Swal.fire({
            icon: "error",
            title: "Error",
            text: message,
          });
        } else {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: auditResponse.error.message,
          });
        }
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err.message,
      });
    }
  }, [auditResponse]);

  const renderContent = () => {
    return (
      <FormPageShell>
        <Box
          component="form"
          noValidate
          onSubmit={handleSubmit(onSubmit)}
        >
          <Stack spacing={2}>
            <FormPageCard>
              <FormPageHeader
                title={
                  authState.editAccess
                    ? "Edit Consent Data"
                    : "View Consent Data"
                }
                onBack={goBack}
                subtitle={
                  authState.editAccess
                    ? "Please fill in the required fields."
                    : undefined
                }
              />

              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                sx={{ mb: 2 }}
              >
                <TextField
                  name="first_name"
                  margin="normal"
                  fullWidth
                  label="First Name*"
                  variant="outlined"
                  inputRef={register}
                  error={errors.first_name != null}
                  helperText={errors.first_name != null ? errors.first_name.message : null}
                  disabled={auditResponse.loading}
                />
                <TextField
                  name="last_name"
                  margin="normal"
                  fullWidth
                  label="Last Name*"
                  variant="outlined"
                  inputRef={register}
                  error={errors.last_name != null}
                  helperText={errors.last_name != null ? errors.last_name.message : null}
                  disabled={auditResponse.loading}
                />
              </Stack>

              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                sx={{ mb: 2 }}
              >
                <TextField
                  name="email_id"
                  margin="normal"
                  fullWidth
                  label="Email*"
                  variant="outlined"
                  inputRef={register}
                  type="email"
                  error={errors.email_id != null}
                  helperText={errors.email_id != null ? errors.email_id.message : null}
                  disabled={auditResponse.loading}
                />
                <TextField
                  name="mobile_number"
                  margin="normal"
                  fullWidth
                  label="Mobile Number*"
                  variant="outlined"
                  inputRef={register}
                  error={errors.mobile_number != null}
                  helperText={errors.mobile_number != null ? errors.mobile_number.message : null}
                  disabled={auditResponse.loading}
                />
              </Stack>

              <Box sx={{ mb: 2 }}>
                <Controller
                  name="consent_withdrawn_date"
                  control={control}
                  defaultValue=""
                  render={({ onChange, value }) => (
                    <DatePicker
                      label="Consent Withdrawn Date"
                      value={value && value !== "" ? moment(value) : null}
                      onChange={(date) => onChange(date ? date.toISOString() : "")}
                      format="DD/MM/YYYY"
                      disabled={auditResponse.loading}
                      slotProps={{
                        field: { clearable: true },
                        textField: {
                          variant: "outlined",
                          fullWidth: true,
                          margin: "normal",
                        },
                      }}
                    />
                  )}
                />
              </Box>

              <TextField
                name="remarks"
                margin="normal"
                fullWidth
                label="Remarks"
                variant="outlined"
                inputRef={register}
                multiline
                rows={3}
                disabled={auditResponse.loading}
                placeholder="Enter remarks here..."
                defaultValue=""
              />
            </FormPageCard>
            <FormPageCard>
              <FormActions>
                <Button
                  type="submit"
                  size="large"
                  variant="contained"
                  color="primary"
                  disabled={auditResponse.loading}
                >
                  Submit
                </Button>
              </FormActions>
            </FormPageCard>
          </Stack>
        </Box>
      </FormPageShell>
    );
  };

  return !response.loading && renderContent();
};

export default EditConsentData;
